import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import {
  type AppliedPropertyFilter,
  deserializeAppliedFilters,
  type PropertyFilterInfoForQueryFilter,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import objectUtils from 'donor-elf-web/lib/object-utils';
import LineItemModel from 'donor-elf-web/models/line-item';
import type { CampaignGoalRouteModel } from 'donor-elf-web/routes/funds/fund/campaigns/campaign/goals/goal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import JournalService from 'donor-elf-web/services/journal-service';
import moment from 'moment';

const PAGE_SIZE = 50;

export default class CampaignsCampaignIndexController extends Controller {
  @service declare campaignService: CampaignService;
  @service declare fundGoalService: FundGoalService;
  @service declare journalService: JournalService;
  @service declare store: Store;

  @tracked model!: CampaignGoalRouteModel;
  queryParams = ['page', 'filters'];

  @tracked page = 1;
  @tracked filters?: string;
  @tracked appliedFilters?: AppliedPropertyFilter[];

  @tracked loading = true;
  @tracked lineItems: LineItemModel[] = [];
  @tracked totalPages = 0;
  @tracked totalAmount = 0;

  get currentCampaignGoal() {
    return this.model.campaignGoal;
  }

  get currentCampaignDashboard() {
    return this.currentCampaignGoal?.campaignGoalDashboard;
  }

  templateDidInsert = () => {
    if (isEmpty(this.filters)) {
      this.filters = this.defaultFilters;
    }
    this.deserializeFilters();
    this.load();
  };

  templateWillDestroy = () => {
    this.loading = true;
    this.filters = undefined;
    this.appliedFilters = undefined;
    this.page = 1;
    this.lineItems = [];
  };

  async load() {
    this.loading = true;
    let queryFilter: any = {
      campaignId: this.model.campaign.id,
      offset: (this.page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    };

    let { availablePropertyFilters } = this;
    for (let appliedFilter of this.appliedFilters || []) {
      let propertyFilter = availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
    }

    let records = await this.store.query('line-item', {
      filter: objectUtils.decamelizeKeys(queryFilter),
    });
    let totalCount = Number(records.meta.total_count || 0);
    this.totalPages = Math.ceil(totalCount / PAGE_SIZE);
    this.totalAmount = Number(records.meta.total_amount);
    this.lineItems = records.slice();
    this.loading = false;
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    let list: PropertyFilterInfoForQueryFilter[] = [];
    list.push({
      propertyName: 'transDate',
      label: 'Date',
      propertyType: 'date',
      availableOperations: ['><', '>=', '<=', '='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.operation === '>=') {
          queryFilter.from = appliedFilter.val;
        } else if (appliedFilter.operation === '<=') {
          queryFilter.to = appliedFilter.val;
        } else if (appliedFilter.operation === '><') {
          let dates = appliedFilter.val as string[];
          queryFilter.from = dates[0];
          queryFilter.to = dates[1];
        } else {
          queryFilter.date = appliedFilter.val;
        }
      },
    });
    list.push({
      propertyName: 'contactIds',
      label: 'Contact',
      propertyType: 'contactIds',
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.contactIds = appliedFilter.val;
      },
    });
    return list;
  }

  get defaultFilters(): string | undefined {
    let appliedFilters: AppliedPropertyFilter[] = [];
    appliedFilters.push({
      propertyName: 'transDate',
      operation: '>=',
      val: moment(this.model.campaignGoal.startDate).format('YYYY-MM-DD'),
    });
    if (this.model.campaignGoal.endDate) {
      appliedFilters.push({
        propertyName: 'transDate',
        operation: '<=',
        val: moment(this.model.campaignGoal.endDate).format('YYYY-MM-DD'),
      });
    }
    return serializeAppliedFilters(appliedFilters);
  }

  handleFiltersChanged = (currentFilters: AppliedPropertyFilter[]) => {
    this.page = 1;
    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.load();
  };

  changePage = (pageNumber: number) => {
    this.page = pageNumber;
    this.load();
  };

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }
}
